import { useState } from 'react';
import { Button } from '../common/Button';
import { useReportOptions } from '../../hooks/useReportOptions';
import { useTranslation } from 'react-i18next';
import { useReportStore } from '../../stores/reportStore';
import { X } from 'lucide-react';
import { useModal } from '../../context/ModalContext';
import FullBlockingModal from '../common/FullBlockingModal';
import { InputGroup } from '../common/InputGroup';
import { DropdownSelect } from '../common/DropdownSelect';

interface ReportModalProps {
  onClose: () => void;
  contentType: 'job' | 'profile' | 'review' | 'quote' | 'quote_request';
  entityId: string;
  reportedId: string;
}

export default function ReportModal({
  onClose,
  contentType,
  entityId,
  reportedId,
}: ReportModalProps) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const [details, setDetails] = useState<string | null>('');

  const reasons = useReportOptions(contentType);
  const { createReport } = useReportStore();
  const { t } = useTranslation(['reports', 'common']);

  const { isAnyModalOpen, setIsAnyModalOpen } = useModal();

  const closeModal = () => {
    onClose();
    setIsAnyModalOpen(!isAnyModalOpen);
  };

  const handleSubmit = async () => {
    if (!selectedReason) return;
    if (selectedReason === 'other' && !reason.trim()) return;

    try {
      setLoading(true);
      await createReport({
        content_type: contentType,
        entity_id: entityId,
        reason: selectedReason === 'Other' || selectedReason === 'Autre' ? reason : selectedReason,
        reported_id: reportedId,
        details: details ? details.trim() : null,
      });
      closeModal();
      setSelectedReason('');
      setReason('');
      setDetails('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FullBlockingModal>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          {t('report_content', { ns: 'reports' })}
        </h2>
        <button
          onClick={closeModal}
          className="text-gray-500 hover:text-gray-800 transition"
          aria-label={t('common:form_actions.cancel', { ns: 'common' })}
        >
          <X size={20} />
        </button>
      </div>

      <p className="text-sm text-gray-600 mb-4">{t('description')}</p>

      <div>
        <InputGroup label={t('reportPlaceholder')}>
          <DropdownSelect
            value={selectedReason}
            onChange={value => setSelectedReason(value)}
            placeholder={t('reportPlaceholder')}
            options={reasons.map(re => t(re.label))}
            getOptionLabel={item => item}
            getOptionValue={item => item}
            className="mt-2"
          />
        </InputGroup>
      </div>

      {(selectedReason === 'Other' || selectedReason === 'Autre') && (
        <div className="mb-4">
          <InputGroup
            label={t('other_reason_label')}
            value={reason}
            onChange={e => setReason(e.target.value)}
            as="textarea"
            placeholder={t('other_reason_placeholder')}
          />
        </div>
      )}

      {/* Buttons */}
      <div className="flex justify-end gap-3 mt-6">
        <Button variant="transparent-with-border" onClick={closeModal}>
          {t('common:form_actions.cancel', { ns: 'common' })}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading || !selectedReason || (selectedReason === 'other' && !reason.trim())}
        >
          {loading ? t('common:form_actions.sending', { ns: 'common' }) : t('report')}
        </Button>
      </div>
    </FullBlockingModal>
  );
}
